import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface CrucibleLidProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Vertical offset above the crucible rim (0 = seated, larger = lifted). */
  liftHeight?: number;
  /** Tilt around Z so the lid tips off the rim when lifted by tongs. */
  tiltZ?: number;
  /** Temperature in °C — controls red-glow emissive (same scale as crucible). */
  temperatureC?: number;
}

/**
 * Porcelain crucible lid — a shallow dish-shaped cover with a central knob.
 * Visible above the crucible body. When `liftHeight` > 0 the lid rises off
 * the rim, and `tiltZ` adds a tongs-style tilt so the user can see fumes
 * escaping briefly.
 */
export function CrucibleLid({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  liftHeight = 0,
  tiltZ = 0,
  temperatureC = 25,
}: CrucibleLidProps) {
  const matRef = useRef<THREE.MeshStandardMaterial>(null);

  useFrame(() => {
    if (matRef.current) {
      const t = Math.max(0, Math.min(1, (temperatureC - 350) / 600));
      matRef.current.emissiveIntensity = t * 0.9;
      const r = 0.85 + 0.15 * t;
      const g = 0.15 + 0.55 * t;
      const b = 0.05 + 0.15 * t;
      matRef.current.emissive.setRGB(r, g, b);
    }
  });

  const lidRadius = 0.085;
  const lidHeight = 0.018;
  const knobRadius = 0.012;
  const knobHeight = 0.014;

  return (
    <group
      position={[position[0], position[1] + liftHeight, position[2]]}
      rotation={[rotation[0], rotation[1], rotation[2] + tiltZ]}
      scale={scale}
    >
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[lidRadius, lidRadius * 0.92, lidHeight, 36]} />
        <meshStandardMaterial
          ref={matRef}
          color="#efe6d6"
          roughness={0.62}
          metalness={0.02}
          emissive="#ff5520"
          emissiveIntensity={0}
        />
      </mesh>
      <mesh position={[0, lidHeight / 2 + 0.001, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[lidRadius * 0.98, 0.003, 8, 28]} />
        <meshStandardMaterial color="#d8cbb2" roughness={0.7} />
      </mesh>
      <mesh position={[0, lidHeight / 2 + knobHeight / 2, 0]} castShadow>
        <cylinderGeometry args={[knobRadius * 0.9, knobRadius, knobHeight, 24]} />
        <meshStandardMaterial color="#e3d6bc" roughness={0.6} metalness={0.05} />
      </mesh>
      <mesh position={[0, lidHeight / 2 + knobHeight + 0.005, 0]}>
        <sphereGeometry args={[knobRadius * 0.7, 16, 16]} />
        <meshStandardMaterial color="#e3d6bc" roughness={0.6} metalness={0.05} />
      </mesh>
    </group>
  );
}

export default CrucibleLid;
